import { useState } from 'react';
import { Pressable, useColorScheme } from 'react-native';
import { View } from 'tamagui';

import { darkColors, lightColors, radius, space } from '@/theme/tokens.generated';
import { hit, slopFor } from '@/lib/hit-slop';

// A controlled on/off switch -- tokens/borders.json already reserved
// radius-semantic.toggle ("Pill track") for this shape, but nothing in
// the app used it until issue #315's demographics opt-out needed a
// real boolean control and grepping the codebase found no existing
// Switch/Toggle to reuse (see this file's own plan task, Step 0).
//
// Built from Pressable + two Views (track + thumb) rather than
// react-native's own <Switch>: that component renders native OS chrome
// (iOS/Android look wildly different, and web gets nothing at all from
// RN's Switch), which would be the one remaining "stock RN chrome"
// surface in an app that already fixed this exact problem for the tab
// bar (issue #147 AC2's own doc comment). This instead uses the same
// resolved-hex-on-a-raw-View pattern glass-surface.tsx/vote-control.tsx
// already established for pieces react-native-svg or plain Views can't
// resolve a Tamagui token string against.
export function Toggle({
  value,
  onValueChange,
  accessibilityLabel,
}: {
  value: boolean;
  onValueChange: (next: boolean) => void;
  accessibilityLabel: string;
}) {
  const [focused, setFocused] = useState(false);
  const colorScheme = useColorScheme();
  const colors = colorScheme === 'dark' ? darkColors : lightColors;

  const trackColor = value ? colors.actionPrimary : colors.borderDefault;

  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityState={{ checked: value }}
      aria-checked={value}
      accessibilityLabel={accessibilityLabel}
      {...hit({ vertical: slopFor(24) })}
      onPress={() => onValueChange(!value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={[
        {
          width: 44,
          height: 24,
          borderRadius: radius.full,
          padding: space['0_5'],
          justifyContent: 'center',
          backgroundColor: trackColor,
        },
        focused
          ? { outlineStyle: 'solid', outlineWidth: 2, outlineColor: colors.actionPrimary, outlineOffset: 2 }
          : null,
      ]}
    >
      <View
        style={{
          width: 20,
          height: 20,
          borderRadius: radius.full,
          backgroundColor: '#ffffff',
          transform: [{ translateX: value ? 20 : 0 }],
        }}
      />
    </Pressable>
  );
}
